import { useCustomToast } from '@/shared/lib';
import type { ToastContentProps } from '@/shared/lib';
import type { ToastOptions } from 'vue-toastification/dist/types/types';

type FormBuilderToastId = number | string;

type ShowToastFn = (
  contentProps: ToastContentProps,
  options?: Partial<ToastOptions>
) => FormBuilderToastId;

export const MAX_ACTIVE_FORM_BUILDER_TOASTS = 3;

const activeToastIds: FormBuilderToastId[] = [];

export function resetFormBuilderToastQueue(): void {
  activeToastIds.length = 0;
}

export function useFormBuilderToast(): {
  showSuccessToast: ShowToastFn;
  showErrorToast: ShowToastFn;
  showWarningToast: ShowToastFn;
  showValidationErrorToast: (validationErrors: { [key: string]: string[] }, title?: string) => void;
  dismissToast: (toastId: FormBuilderToastId) => void;
} {
  const toast = useCustomToast();

  const untrack = (toastId: FormBuilderToastId | undefined): void => {
    if (toastId === undefined || toastId === null) return;

    const index = activeToastIds.indexOf(toastId);
    if (index !== -1) {
      activeToastIds.splice(index, 1);
    }
  };

  const dismissOldestWhileFull = (): void => {
    while (activeToastIds.length >= MAX_ACTIVE_FORM_BUILDER_TOASTS) {
      const oldestToastId = activeToastIds.shift();
      if (oldestToastId !== undefined) {
        toast.dismissToast(oldestToastId);
      }
    }
  };

  const withQueue =
    (show: ShowToastFn): ShowToastFn =>
    (contentProps: ToastContentProps, options: Partial<ToastOptions> = {}): FormBuilderToastId => {
      dismissOldestWhileFull();

      const toastId: FormBuilderToastId = show(contentProps, {
        ...options,
        onClose: () => {
          untrack(toastId);
          options.onClose?.();
        },
      });

      if (toastId !== undefined && toastId !== null) {
        activeToastIds.push(toastId);
      }

      return toastId;
    };

  return {
    showSuccessToast: withQueue(toast.showSuccessToast),
    showErrorToast: withQueue(toast.showErrorToast),
    showWarningToast: withQueue(toast.showWarningToast),
    showValidationErrorToast: toast.showValidationErrorToast,
    dismissToast: (toastId: FormBuilderToastId): void => {
      untrack(toastId);
      toast.dismissToast(toastId);
    },
  };
}
